import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  CircularProgress,
  Paper,
} from "@mui/material";
import apiService from "../../../../../../service/Api";


export interface DonationResponse {
  id: number;
  reference: string;
  donor_name: string | null;
  display_name: string | null;
  email: string | null;
  phone: string | null;
  is_anonymous: boolean;
  amount: string;
  currency: string;
  payment_method: string | null;
  paid: boolean;
  created_at: string;
}

interface DonationsTableProps {
  streamId?: number | string;
  title?: string;
  paid?: boolean;
  currency?: string;
  ordering?: string;
  refreshKey?: number;
}

const DonationsTable: React.FC<DonationsTableProps> = ({
  streamId,
  title = "All Donations",
  paid,
  currency,
  ordering = "-created_at",
  refreshKey = 0,
}) => {
  const [donations, setDonations] = useState<DonationResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDonations = async () => {
      if (!streamId) return;

      setLoading(true);
      setError(null);
      try {
        const params: Record<string, string | number> = {
          stream_id: streamId,
        };

        if (paid !== undefined) {
          params.paid = paid.toString();
        }
        if (currency) {
          params.currency = currency;
        }
        if (ordering) {
          params.ordering = ordering;
        }

        const response = await apiService.get<DonationResponse[] | { results: DonationResponse[] }>(
          "/api/chema/donations/",
          params
        );

        // Handle both paginated and non-paginated responses
        const data = response.data;
        if (Array.isArray(data)) {
          setDonations(data);
        } else if (data && 'results' in data) {
          setDonations(data.results);
        } else {
          setDonations([]);
        }
      } catch (err: unknown) {
        console.error("Error fetching donations:", err);
        const errorMessage = err instanceof Error ? err.message : "Failed to fetch donations";
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchDonations();
  }, [streamId, paid, currency, ordering, refreshKey]);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const year = date.getFullYear().toString().slice(-2);
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const day = date.getDate().toString().padStart(2, "0");
      return `${year}/${month}/${day}`;
    } catch {
      return dateString;
    }
  };

  const getDonationStatus = (donation: DonationResponse): "Paid" | "Cancelled" => {
    return donation.paid ? "Paid" : "Cancelled";
  };

  const getStatusStyle = (status: "Paid" | "Cancelled") => {
    return status === "Paid"
      ? { bg: "#d1fae5", color: "#065f46" }
      : { bg: "#fee2e2", color: "#991b1b" };
  };

  const formatPaymentMethod = (method: string | null) => {
    if (!method) return "N/A";
    const methodMap: Record<string, string> = {
      ecocash: "EcoCash",
      onemoney: "OneMoney",
      innbucks: "InnBucks",
    };
    return methodMap[method.toLowerCase()] || method;
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
        {title}
      </Typography>

      {donations.length === 0 ? (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">No donations found</Typography>
        </Box>
      ) : (
        <TableContainer sx={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <Table
            sx={{
              minWidth: 640,
              "& td, & th": {
                borderBottom: "1px solid #e5e7eb",
                whiteSpace: "nowrap",
              },
            }}
          >
            <TableHead>
              <TableRow sx={{ bgcolor: "#f8f9fa" }}>
                <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Donor Name</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Payment Method</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Amount</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {donations.map((donation) => {
                const status = getDonationStatus(donation);
                const statusStyle = getStatusStyle(status);
                return (
                  <TableRow key={donation.id} hover>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: donation.paid ? "#10b981" : "#9ca3af",
                          }}
                        />
                        {formatDate(donation.created_at)}
                      </Box>
                    </TableCell>

                    <TableCell sx={{ fontWeight: 500 }}>
                      {donation.display_name || donation.donor_name || "Anonymous"}
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={status}
                        size="small"
                        sx={{
                          bgcolor: statusStyle.bg,
                          color: statusStyle.color,
                          fontWeight: 500,
                          borderRadius: "12px",
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      {formatPaymentMethod(donation.payment_method)}
                    </TableCell>

                    <TableCell sx={{ fontWeight: "bold" }}>
                      {donation.currency === "ZWL" ? "ZWL" : "$"}
                      {parseFloat(donation.amount || "0").toFixed(2)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default DonationsTable;